import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { User } from '@client/components/_ui/display/User';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useUser } from '@client/hooks/business/user/useUser';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs } from '@client/trpc/client';
import {
    SERVICE_API_TOKEN_EXPIRY_WARNING_DAYS,
    SERVICE_API_TOKEN_SCOPE_LABELS,
    type ServiceApiTokenScope,
} from '@shared/constants/system/service-api-token';
import { hasPermission } from '@shared/permissions/roles';
import { createFileRoute } from '@tanstack/react-router';
import { Tag, Tooltip } from 'antd';
import { CalendarClock, Key, Plus, RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { CreateTokenModal } from './components/CreateTokenModal';
import { RenewTokenModal } from './components/RenewTokenModal';
import { RevokeTokenModal } from './components/RevokeTokenModal';
import { RotateTokenModal } from './components/RotateTokenModal';
import { ServiceApiDisabledCallout } from './components/ServiceApiDisabledCallout';
import { ServiceApiTokenFilters } from './components/ServiceApiTokenFilters';
import { serviceApiTokenDaysUntilExpiry } from './components/service-api-token-expiry';
import { TokenSecretModal } from './components/TokenSecretModal';

export const Route = createFileRoute('/console/$orgId/configuration/api-integrations/api-tokens')({
    staticData: {
        breadcrumb: { title: 'API tokens' },
        ui: {
            icon: Key,
            title: 'API tokens',
            subtitle: 'Manage service API tokens for external integrations',
            menuDescription: 'Manage API tokens for external system integrations',
        },
        permissions: ['service_api_token:read'],
    },
    component: ApiTokensPage,
});

function ApiTokensPage() {
    const user = useUser();
    const canCreate = hasPermission(user.roles, 'service_api_token:create');
    const canUpdate = hasPermission(user.roles, 'service_api_token:update');
    const canDelete = hasPermission(user.roles, 'service_api_token:delete');

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [revokeToken, setRevokeToken] = useState<{ id: bigint; name: string } | null>(null);
    const [renewToken, setRenewToken] = useState<{ id: bigint; name: string } | null>(null);
    const [rotateToken, setRotateToken] = useState<{ id: bigint; name: string } | null>(null);
    const [newTokenSecret, setNewTokenSecret] = useState<{
        token: string;
        name: string;
        expiresAt: Date;
    } | null>(null);

    return (
        <PageController route={Route}>
            <TRPCErrorBoundary>
                <ApiTokensContent
                    canCreate={canCreate}
                    canUpdate={canUpdate}
                    canDelete={canDelete}
                    onCreateClick={() => setCreateModalOpen(true)}
                    onRevokeClick={(id, name) => setRevokeToken({ id, name })}
                    onRenewClick={(id, name) => setRenewToken({ id, name })}
                    onRotateClick={(id, name) => setRotateToken({ id, name })}
                />
            </TRPCErrorBoundary>

            <CreateTokenModal
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onSuccess={(token, name, expiresAt) => {
                    setCreateModalOpen(false);
                    setNewTokenSecret({ token, name, expiresAt });
                }}
            />

            <RevokeTokenModal token={revokeToken} onClose={() => setRevokeToken(null)} />

            <RenewTokenModal token={renewToken} onClose={() => setRenewToken(null)} />

            <RotateTokenModal
                token={rotateToken}
                onClose={() => setRotateToken(null)}
                onSuccess={(token, name, expiresAt) => {
                    setRotateToken(null);
                    setNewTokenSecret({ token, name, expiresAt });
                }}
            />

            <TokenSecretModal tokenInfo={newTokenSecret} onClose={() => setNewTokenSecret(null)} />
        </PageController>
    );
}

interface ApiTokensContentProps {
    canCreate: boolean;
    canUpdate: boolean;
    canDelete: boolean;
    onCreateClick: () => void;
    onRevokeClick: (id: bigint, name: string) => void;
    onRenewClick: (id: bigint, name: string) => void;
    onRotateClick: (id: bigint, name: string) => void;
}

function ApiTokensContent({
    canCreate,
    canUpdate,
    canDelete,
    onCreateClick,
    onRevokeClick,
    onRenewClick,
    onRotateClick,
}: ApiTokensContentProps) {
    const { trpc } = useTrpc();
    const { formatDate, formatDateTime } = useFormatter();

    const [queryParams, setQueryParams] = useState<RouterInputs['serviceApiToken']['list']>({
        orderBy: { createdAt: 'desc' },
    });

    // Handle filter changes
    const handleFilterChange = useCallback(
        (where: NonNullable<RouterInputs['serviceApiToken']['list']['where']>, includeInactive: boolean) => {
            setQueryParams((prev) => ({
                ...prev,
                where,
                includeInactive,
                offset: 0, // Reset pagination when filters change
            }));
        },
        [],
    );

    const { data, isLoading } = trpc.serviceApiToken.list.useQuery(queryParams);

    // Token management (create / rotate / renew) is refused server-side for orgs whose Service API is
    // switched off in the system console. Read the flag up front so the page explains the state
    // instead of surfacing the refusal only after the user submits. Revoke stays available.
    // Fails open: the server guard is authoritative, so while the flag is loading or unavailable the
    // actions stay usable rather than silently locking the page.
    const { data: enablement } = trpc.serviceApiToken.isServiceApiEnabled.useQuery();
    const serviceApiDisabled = enablement?.enabled === false;
    const canManage = canUpdate && !serviceApiDisabled;

    const columns = [
        {
            key: 'name',
            title: 'Name',
            render: (_: unknown, record: NonNullable<typeof data>['tokens'][number]) => (
                <div className="flex items-center gap-2">
                    <Key className="h-4 w-4 text-subtle-foreground" />
                    <span className="font-medium">{record.name}</span>
                </div>
            ),
        },
        {
            key: 'tokenPrefix',
            title: 'Token',
            render: (_: unknown, record: NonNullable<typeof data>['tokens'][number]) => (
                <code className="rounded bg-muted px-2 py-1 text-xs">{record.tokenPrefix}...</code>
            ),
        },
        {
            key: 'scopes',
            title: 'Scopes',
            render: (_: unknown, record: NonNullable<typeof data>['tokens'][number]) => (
                <div className="flex flex-wrap gap-1">
                    {(record.scopes as ServiceApiTokenScope[]).map((scope) => (
                        <Tag key={scope} className="text-xs" variant="outlined">
                            {SERVICE_API_TOKEN_SCOPE_LABELS[scope] ?? scope}
                        </Tag>
                    ))}
                </div>
            ),
        },
        {
            key: 'isActive',
            title: 'Status',
            render: (_: unknown, record: NonNullable<typeof data>['tokens'][number]) => (
                <StatusBadge
                    status={record.isActive ? 'active' : 'revoked'}
                    module="serviceApiToken"
                    variant="outlined"
                />
            ),
        },
        {
            key: 'expiresAt',
            title: 'Expires',
            render: (_: unknown, record: NonNullable<typeof data>['tokens'][number]) => {
                const daysUntilExpiry = serviceApiTokenDaysUntilExpiry(record.expiresAt);
                const isExpired = daysUntilExpiry <= 0;
                const isExpiringSoon = !isExpired && daysUntilExpiry <= SERVICE_API_TOKEN_EXPIRY_WARNING_DAYS;
                return (
                    <span
                        className={
                            isExpired ? 'text-status-danger' : isExpiringSoon ? 'text-status-warning' : undefined
                        }
                    >
                        {formatDate(record.expiresAt)}
                        {isExpired && ' (expired)'}
                        {isExpiringSoon && ' (expires soon)'}
                    </span>
                );
            },
        },
        {
            key: 'lastUsedAt',
            title: 'Last used',
            render: (_: unknown, record: NonNullable<typeof data>['tokens'][number]) => {
                if (!record.lastUsedAt) return <span className="text-subtle-foreground">Never</span>;
                return (
                    <Tooltip title={record.lastUsedIp ? `IP: ${record.lastUsedIp}` : undefined}>
                        <span>{formatDateTime(record.lastUsedAt)}</span>
                    </Tooltip>
                );
            },
        },
        {
            key: 'createdAt',
            title: 'Created at',
            render: (_: unknown, record: NonNullable<typeof data>['tokens'][number]) => formatDate(record.createdAt),
        },
        {
            key: 'createdBy',
            title: 'Created by',
            render: (_: unknown, record: NonNullable<typeof data>['tokens'][number]) =>
                record.createdBy ? (
                    <User
                        userId={BigInt(record.createdBy.id)}
                        name={[record.createdBy.firstName, record.createdBy.lastName].filter(Boolean).join(' ')}
                    />
                ) : null,
        },
        {
            key: 'actions',
            title: '',
            width: 160,
            render: (_: unknown, record: NonNullable<typeof data>['tokens'][number]) => {
                const daysUntilExpiry = serviceApiTokenDaysUntilExpiry(record.expiresAt);
                const isExpiringSoon = daysUntilExpiry > 0 && daysUntilExpiry <= SERVICE_API_TOKEN_EXPIRY_WARNING_DAYS;
                return (
                    <Space size="small">
                        {canManage && record.isActive && isExpiringSoon && (
                            <Button.Icon
                                icon={<CalendarClock />}
                                color="orange"
                                size="small"
                                tooltip="Renew token"
                                aria-label="Renew token"
                                onClick={() => onRenewClick(record.id, record.name)}
                            />
                        )}
                        {canManage && record.isActive && (
                            <Tooltip title="Rotate token">
                                <Button
                                    type="tertiary"
                                    size="small"
                                    icon={<RotateCcw />}
                                    aria-label="Rotate token"
                                    onClick={() => onRotateClick(record.id, record.name)}
                                />
                            </Tooltip>
                        )}
                        {canDelete && record.isActive && (
                            <Tooltip title="Revoke token">
                                <Button
                                    type="danger"
                                    size="small"
                                    onClick={() => onRevokeClick(record.id, record.name)}
                                >
                                    Revoke
                                </Button>
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
    ];

    const tableProps = usePrismaTable({
        data: data?.tokens ?? [],
        total: data?.total ?? 0,
        loading: isLoading,
        columns,
        rowKey: (row) => String(row.id),
        queryParams,
        onParamsChange: setQueryParams,
    });

    return (
        <div className="space-y-4">
            {serviceApiDisabled && <ServiceApiDisabledCallout />}
            <div className="flex items-center justify-between">
                <p className="m-0 text-muted-foreground">
                    Service API tokens allow external systems to interact with the Curium API securely.
                </p>
                {canCreate && (
                    <Button type="primary" icon={<Plus />} onClick={onCreateClick} disabled={serviceApiDisabled}>
                        Create token
                    </Button>
                )}
            </div>

            <ServiceApiTokenFilters onChange={handleFilterChange} />

            <PrismaTable {...tableProps} size="small" />
        </div>
    );
}
